import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import JsBarcode from "jsbarcode";
import { getAuthUser } from "./auth";
import "./LabelsPage.css";

const API_URL = process.env.REACT_APP_API_URL || "https://api.deskpos.ie";

// Helper Function for DRS on labels

const getLabelDrsAmount = (product) => {
  const flag = product.is_drs ?? product.isDrs;
  const savedAmount = Number(product.drs_amount ?? product.drsAmount ?? 0);

  const isDrs =
    flag == null
      ? savedAmount > 0
      : ["true", "1"].includes(String(flag).trim().toLowerCase());

  if (!isDrs) return 0;

  if (Number.isFinite(savedAmount) && savedAmount > 0) {
    return savedAmount;
  }

  const sizeMl = Number(
    product.container_size_ml ?? product.containerSizeMl ?? 0,
  );

  if (sizeMl >= 150 && sizeMl <= 500) return 0.15;
  if (sizeMl > 500 && sizeMl <= 3000) return 0.25;

  return 0;
};

function LabelsPage() {
  const user = getAuthUser();

  const shopId =
    Number(user?.shop_id || user?.shopId || localStorage.getItem("shop_id")) ||
    null;

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [labelItems, setLabelItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!shopId) return;

    const loadProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const token =
          localStorage.getItem("token") || localStorage.getItem("authToken");

        const response = await axios.get(`${API_URL}/products`, {
          params: {
            shop_id: shopId,
          },
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        });

        const result = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.products)
            ? response.data.products
            : [];

        setProducts(result);
      } catch (err) {
        console.error("Labels product load error:", err);

        setError(err.response?.data?.error || "Products could not be loaded.");
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [shopId]);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();

    if (!term) {
      return [];
    }

    return products
      .filter((product) => {
        const name = String(product.name || "").toLowerCase();

        const barcode = String(product.barcode || "").toLowerCase();

        return name.includes(term) || barcode.includes(term);
      })
      .slice(0, 10);
  }, [products, search]);

  const addProduct = (product) => {
    setLabelItems((previous) => {
      const existing = previous.find((item) => item.id === product.id);

      if (existing) {
        return previous.map((item) =>
          item.id === product.id
            ? {
                ...item,
                labelQty: Number(item.labelQty || 1) + 1,
              }
            : item,
        );
      }

      return [
        ...previous,
        {
          id: product.id,
          name: product.name,
          barcode: product.barcode || "",
          price: Number(product.price || 0),
          stock: Number(product.stock || 0),
          drs_amount: getLabelDrsAmount(product),
          labelQty: 1,
        },
      ];
    });

    setSearch("");
  };

  const updateLabelQty = (id, value) => {
    const quantity = Math.max(1, Number(value || 1));

    setLabelItems((previous) =>
      previous.map((item) =>
        item.id === id
          ? {
              ...item,
              labelQty: quantity,
            }
          : item,
      ),
    );
  };

  const increaseLabelQty = (id) => {
    setLabelItems((previous) =>
      previous.map((item) =>
        item.id === id
          ? {
              ...item,
              labelQty: Number(item.labelQty || 1) + 1,
            }
          : item,
      ),
    );
  };

  const decreaseLabelQty = (id) => {
    setLabelItems((previous) =>
      previous.map((item) =>
        item.id === id
          ? {
              ...item,
              labelQty: Math.max(1, Number(item.labelQty || 1) - 1),
            }
          : item,
      ),
    );
  };

  const removeLabelItem = (id) => {
    setLabelItems((previous) => previous.filter((item) => item.id !== id));
  };

  const clearLabels = () => {
    setLabelItems([]);
    setSearch("");
  };

  const totalLabels = labelItems.reduce(
    (sum, item) => sum + Number(item.labelQty || 0),
    0,
  );

  // =========================================
  // A4 LABEL PREVIEW
  // =========================================

  const previewLabels = () => {
    if (labelItems.length === 0) {
      return;
    }

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const labelWidth = 70;
    const labelHeight = 35;

    const columns = 3;
    const rows = 8;

    const labelsPerPage = columns * rows;

    const startX = 0;
    const startY = 8;

    const printableLabels = [];

    labelItems.forEach((item) => {
      const quantity = Math.max(1, Number(item.labelQty || 1));

      for (let index = 0; index < quantity; index += 1) {
        printableLabels.push({
          ...item,
        });
      }
    });

    const createBarcodeImage = (barcode) => {
      if (!barcode) {
        return null;
      }

      try {
        const canvas = document.createElement("canvas");

        JsBarcode(canvas, String(barcode), {
          format: "CODE128",
          displayValue: false,
          margin: 0,
          height: 28,
          width: 1.4,
        });

        return canvas.toDataURL("image/png");
      } catch (error) {
        console.error("Barcode generation failed:", barcode, error);

        return null;
      }
    };

    printableLabels.forEach((item, index) => {
      const positionOnPage = index % labelsPerPage;

      const pageIndex = Math.floor(index / labelsPerPage);

      if (positionOnPage === 0 && pageIndex > 0) {
        doc.addPage("a4", "portrait");
      }

      const row = Math.floor(positionOnPage / columns);

      const column = positionOnPage % columns;

      const x = startX + column * labelWidth;

      const y = startY + row * labelHeight;

      // =========================
      // LABEL BORDER
      // =========================

      doc.setDrawColor(170);

      doc.rect(x, y, labelWidth, labelHeight);
      // =========================
      // GREY TOP BAR
      // =========================

      doc.setFillColor(235, 237, 240);

      doc.rect(x, y, labelWidth, 4, "F");

      // =========================
      // PRODUCT NAME
      // =========================

      doc.setFont("helvetica", "bold");

      doc.setFontSize(8.5);

      const productNameLines = doc.splitTextToSize(
        String(item.name || ""),
        labelWidth - 8,
      );

      const visibleNameLines = productNameLines.slice(0, 2);

      const nameStartY = visibleNameLines.length > 1 ? y + 7 : y + 8.5;

      visibleNameLines.forEach((line, lineIndex) => {
        doc.text(line, x + labelWidth / 2, nameStartY + lineIndex * 3.2, {
          align: "center",
        });
      });

      // Deposit underneath the product name.
      const drsAmount = Number(item.drs_amount || 0);

      if (drsAmount > 0) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(7);

        doc.text(
          `+ €${drsAmount.toFixed(2)} DRS Deposit`,
          x + labelWidth / 2,
          y + 14,
          { align: "center" },
        );
      }

      // =========================
      // PRICE
      // =========================

      doc.setFont("helvetica", "bold");

      doc.setFontSize(17);

      doc.text(
        `€${Number(item.price || 0).toFixed(2)}`,
        x + labelWidth / 2,
        y + 20,
        {
          align: "center",
        },
      );

      // =========================
      // BARCODE
      // =========================

      if (item.barcode) {
        const barcodeImage = createBarcodeImage(item.barcode);

        if (barcodeImage) {
          doc.addImage(
            barcodeImage,
            "PNG",
            x + 14,
            y + 22,
            labelWidth - 28,
            6.5,
          );
        }

        doc.setFont("helvetica", "normal");

        doc.setFontSize(6.5);

        doc.text(String(item.barcode), x + labelWidth / 2, y + 32, {
          align: "center",
        });
      }
    });

    const pdfBlob = doc.output("blob");

    const pdfUrl = URL.createObjectURL(pdfBlob);

    window.open(pdfUrl, "_blank");

    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 60000);
  };

  return (
    <div className="labels-page">
      <div className="labels-header">
        <div>
          <h1>Labels</h1>

          <p>
            Create shelf labels for products and prepare them for A4 printing.
          </p>
        </div>

        <div className="labels-total-card">
          <span>Total Labels</span>

          <strong>{totalLabels}</strong>
        </div>
      </div>

      <section className="labels-card">
        <div className="labels-card-heading">
          <div>
            <h2>Add Products</h2>

            <p>Search by product name or barcode.</p>
          </div>
        </div>

        <div className="labels-search-wrapper">
          <label htmlFor="label-product-search">Search Product</label>

          <input
            id="label-product-search"
            type="text"
            value={search}
            placeholder="Search product name or barcode"
            onChange={(event) => setSearch(event.target.value)}
            autoComplete="off"
          />

          {search.trim() && filteredProducts.length > 0 && (
            <div className="labels-search-results">
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  type="button"
                  className="labels-search-result"
                  onClick={() => addProduct(product)}
                >
                  <div>
                    <strong>{product.name}</strong>

                    <span>{product.barcode || "No barcode"}</span>
                  </div>

                  <div className="labels-result-meta">
                    <strong>€{Number(product.price || 0).toFixed(2)}</strong>

                    <span>Stock: {Number(product.stock || 0)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}

          {search.trim() && !loading && filteredProducts.length === 0 && (
            <div className="labels-no-result">No matching products found.</div>
          )}
        </div>

        {error && <div className="labels-error">{error}</div>}
      </section>

      <section className="labels-card">
        <div className="labels-card-heading labels-queue-heading">
          <div>
            <h2>Label Queue</h2>

            <p>Choose how many labels to print for each product.</p>
          </div>

          {labelItems.length > 0 && (
            <button
              type="button"
              className="labels-clear-btn"
              onClick={clearLabels}
            >
              Clear All
            </button>
          )}
        </div>

        {labelItems.length === 0 ? (
          <div className="labels-empty">
            <strong>No products selected</strong>

            <span>
              Search for a product above to add it to the label queue.
            </span>
          </div>
        ) : (
          <div className="labels-table-wrapper">
            <table className="labels-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Barcode</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Labels</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {labelItems.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.name}</strong>
                    </td>

                    <td>{item.barcode || "—"}</td>

                    <td>€{Number(item.price || 0).toFixed(2)}</td>

                    <td>{Number(item.stock || 0)}</td>

                    <td>
                      <div className="labels-qty-control">
                        <button
                          type="button"
                          onClick={() => decreaseLabelQty(item.id)}
                        >
                          −
                        </button>

                        <input
                          type="number"
                          min="1"
                          value={item.labelQty}
                          onChange={(event) =>
                            updateLabelQty(item.id, event.target.value)
                          }
                        />

                        <button
                          type="button"
                          onClick={() => increaseLabelQty(item.id)}
                        >
                          +
                        </button>
                      </div>
                    </td>

                    <td>
                      <button
                        type="button"
                        className="labels-remove-btn"
                        onClick={() => removeLabelItem(item.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="labels-actions">
          <div>
            <span>Total labels</span>

            <strong>{totalLabels}</strong>
          </div>

          <button
            type="button"
            className="labels-preview-btn"
            disabled={labelItems.length === 0}
            onClick={previewLabels}
          >
            Preview Labels
          </button>
        </div>
      </section>
    </div>
  );
}

export default LabelsPage;
